<template>
  <FlexCol class="gap-5">
    <FlexRow
      class="gap-4 align-items-center flex-wrap"
      @click="updateEditSection('description')"
    >
      <FlexRow class="gap-4 align-items-center">
        <label for="description" class="text-body-secondary">Description</label>
        <FlexCol class="align-items-end position-relative">
          <input
            type="text"
            class="text-input description-input m-0"
            id="description"
            v-model="store.description"
            @input="store.description = useStringInput(store.description, 40)"
            :class="{
              'border-primary':
                store.description.length <= 0 || store.description.length > 40,
            }"
          />
          <small
            class="position-absolute end-0"
            style="bottom: -24px"
            :class="[store.description.length === 40 ? 'd-block' : 'd-none']"
          >
            (That's the limit)
          </small>
        </FlexCol>
      </FlexRow>
      <FlexRow class="gap-4 align-items-center">
        <input
          type="hidden"
          class="color-picker"
          v-init-picker:[store.textColor]
          v-model="store.textColor"
          style="height: 4rem; width: 4rem"
        />
        <FlexCol class="bg-grey rounded px-2">
          <label class="text-body-secondary">Colour</label>
          <span>{{ store.textColor }}</span>
        </FlexCol>
      </FlexRow>
    </FlexRow>
    <FlexRow class="gap-5">
      <!-- Left side -->
      <FlexCol class="gap-4 flex-grow-1">
        <FlexCol class="gap-2 flex-grow-1" @click="updateEditSection('stamps')">
          <label for="stampIcon" class="text-body-secondary">Stamp Icon</label>
          <ImagePicker
            id="stamp-icon"
            :background="store.backgroundColor"
            :textColor="store.textColor"
            :imageUrl="store.stampImageUrl"
            :text="store.stampText"
            :enable-reset="true"
            @use-logo="handleUseLogo"
            @update-image="
              (image) => {
                store.stampImageUrl = image;
                store.stampText = '';
              }
            "
            @update-text="
              (text) => {
                store.stampText = text;
                store.stampImageUrl = '';
              }
            "
          />
        </FlexCol>

        <FlexCol
          class="gap-2 flex-grow-1"
          v-if="windowSize <= 1400"
          @click="updateEditSection('stamps')"
        >
          <label for="stampCardBackground" class="text-body-secondary">
            Number Of Stamps
          </label>
          <div>
            <SlNumberInput
              v-model="store.totalStamps"
              input-type="number"
              :custom-class="{
                'is-valid': isTotalStampsValid,
                'is-invalid': !isTotalStampsValid,
              }"
              @input="
                store.totalStamps = useNumericInput(store.totalStamps, 0, 15)
              "
            />
            <small class="text-body-tertiary"
              >Choose between 3 to 15 stamps</small
            >
            <small
              v-if="
                store.totalStamps < brand?.stpc.modifier && !props.isOnboarding
              "
              class="text-primary"
              >This value cannot be lower than the number of stamps per
              spend.</small
            >
          </div>
        </FlexCol>

        <FlexCol class="gap-2 flex-grow-1" @click="updateEditSection('none')">
          <label for="stampCardBackground" class="text-body-secondary">
            Stamp Card Background
          </label>
          <SettingCard>
            <ColorGradientPicker />
          </SettingCard>
        </FlexCol>

        <FlexCol class="gap-2 flex-grow-1" @click="updateEditSection('stamps')">
          <label for="stampCardBackground" class="text-body-secondary">
            Before Stamped Background
          </label>
          <SettingCard class="gap-4">
            <FlexRow class="gap-4 justify-content-center">
              <div
                v-for="i in [1, 2, 3, 4]"
                :key="i"
                class="border rounded-circle"
                style="width: 80px; height: 80px"
                :style="{
                  background: store.beforeStampBackgroundColor,
                }"
              ></div>
            </FlexRow>
            <FlexRow class="gap-2">
              <input
                type="hidden"
                class="color-picker"
                v-init-picker:[store.beforeStampBackgroundColor]
                v-model="store.beforeStampBackgroundColor"
                style="height: 4rem; width: 4rem"
              />
              <FlexCol class="bg-grey rounded px-2">
                <label class="text-body-secondary">Colour</label>
                <span>{{ store.beforeStampBackgroundColor }}</span>
              </FlexCol>
            </FlexRow>
          </SettingCard>
        </FlexCol>

        <FlexCol class="gap-2 flex-grow-1" @click="updateEditSection('footer')">
          <label for="footer" class="text-body-secondary"
            >Other Detail (Footer)</label
          >
          <input
            type="text"
            class="form-control"
            placeholder="Terms & Condition Apply"
            autocapitalize="true"
            v-model="store.footer"
            @input="store.footer = useStringInput(store.footer, 40)"
          />
        </FlexCol>

        <FlexCol
          class="gap-2 flex-grow-1"
          @click="updateEditSection('front-card')"
        >
          <label for="frontCard" class="text-body-secondary">Front Card</label>
          <ImagePicker
            id="front-card"
            :emoji-picker="false"
            :background="store.frontImageUrl ? store.backgroundColor : 'white'"
            textColor="black"
            :imageUrl="store.frontImageUrl"
            :text="
              props.brandName
                ? props.brandName.charAt(0).toUpperCase()
                : store.frontLogoText
            "
            :aspect-ratio="1.61"
            @use-logo="handleUseLogoFront"
            @update-image="
              (image) => {
                store.frontImageUrl = image;
                store.frontUseLogo = false;
              }
            "
          />
        </FlexCol>
      </FlexCol>
      <!-- right side -->
      <FlexCol
        class="gap-2"
        v-if="windowSize > 1400"
        @click="updateEditSection('stamps')"
      >
        <label for="numberOfStamps" class="text-body-secondary fw-500">
          Number Of Stamps
        </label>
        <div>
          <SlNumberInput
            v-model="store.totalStamps"
            input-type="number"
            :custom-class="{
              'is-valid': isTotalStampsValid,
              'is-invalid': !isTotalStampsValid,
            }"
            @input="
              store.totalStamps = useNumericInput(store.totalStamps, 0, 15)
            "
          />
          <small class="text-body-tertiary"
            >Choose between 3 to 15 stamps</small
          >
        </div>
        <small
          v-if="store.totalStamps < brand?.stpc.modifier && !props.isOnboarding"
          class="text-primary"
          >This value cannot be lower than <br />
          the number of stamps per spend.</small
        >
      </FlexCol>
    </FlexRow>
    <FlexCol class="gap-2 flex-grow-1" @click="updateEditSection('none')">
      <CardExpiryToggle />
    </FlexCol>
  </FlexCol>
</template>
<script setup>
import FlexCol from "@/layout/FlexCol.vue";
import FlexRow from "@/layout/FlexRow.vue";
import { vInitPicker } from "@/stores/stampCard";
import SettingCard from "../Components/SettingCard.vue";
import ColorGradientPicker from "../Components/ColorGradientPicker.vue";
import CardExpiryToggle from "../Components/CardExpiryToggle.vue";
import { useStampCardStore } from "@/stores/stampCard";
import { useBrandStore } from "@/stores/brand";
import { useStringInput, useNumericInput } from "@/constants/formValidator.js";
import ImagePicker from "../Components/ImagePicker.vue";
import SlNumberInput from "@/components/forms/input/SlNumberInput.vue";
import { ref, onMounted, onUnmounted, computed } from "vue";

const props = defineProps({
  brandName: {
    type: String,
    required: false,
  },
  isOnboarding: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(["show-front-card", "editing-expiry"]);

const store = useStampCardStore();
const { brand } = useBrandStore();

const windowSize = ref(window.innerWidth);

const isTotalStampsValid = computed(() => {
  return (
    store.totalStamps >= 3 &&
    store.totalStamps <= 15 &&
    (props.isOnboarding || store.totalStamps >= brand?.stpc.modifier)
  );
});

const updateEditSection = (section) => {
  store.editSection = section === "front-card" ? "none" : section;
  emit("show-front-card", section === "front-card");
};

const handleUseLogo = () => {
  const { brandLogo } = store;

  if (brandLogo) {
    store.stampImageUrl = brandLogo;
    store.stampText = "";
  } else {
    store.stampText = props.brandName
      ? props.brandName.charAt(0).toUpperCase()
      : brand?.name.charAt(0).toUpperCase();
    store.stampImageUrl = "";
  }
};

const handleUseLogoFront = () => {
  const { brandLogo } = store;
  if (brandLogo) {
    store.frontImageUrl = brandLogo;
  } else {
    store.frontImageUrl = "";
  }
};

onMounted(() => {
  window.addEventListener("resize", () => {
    windowSize.value = window.innerWidth;
  });
});

onUnmounted(() => {
  window.removeEventListener("resize", () => {
    windowSize.value = window.innerWidth;
  });
  // To remove all the color picker initialized when the page is unmounted
  const colorPickers = document.querySelectorAll(".pcr-app ");
  colorPickers.forEach((picker) => {
    picker.remove();
  });
});
</script>
<style scoped>
:deep(.pcr-button) {
  border: 1px solid var(--secondary-blue);
  width: 3.5rem;
  height: 3.5rem;
}

:deep(.bg-grey) {
  background-color: #f6f7f5;
}

:deep(.description-input) {
  border: none;
  border-bottom: 0.0625rem solid #d2d4e4;
  min-height: 3.5rem;
}
</style>
